import { useState, type ReactElement } from "react";

const centers = ["总指挥台", "审批中心", "任务中枢", "商品中心", "系统中心"] as const;
type Center = (typeof centers)[number];

const centerDescriptions: Readonly<Record<Center, string>> = {
  审批中心: "逐张查看待审批事项、版本差异、证据、风险与许可范围。",
  商品中心: "按图案家族管理长方形、走廊形和圆形三种产品。",
  总指挥台: "用对话提出目标，先形成结构化操作草案，再决定是否建立任务。",
  任务中枢: "查看流程阶段、等待原因、成本、重试、暂停和人工接管状态。",
  系统中心: "查看系统健康、暂停、设备、权限、连接器、审计与版本。",
};

export interface RtrugsShellProps {
  readonly apiBaseUrl: string;
  readonly surface: "desktop" | "web";
}

export function RtrugsShell({ apiBaseUrl, surface }: RtrugsShellProps): ReactElement {
  const [activeCenter, setActiveCenter] = useState<Center>("总指挥台");
  const [draft, setDraft] = useState("");

  return (
    <div className="rtrugs-shell">
      <header className="topbar">
        <div className="brand-lockup">
          <span className="brand-mark" aria-hidden="true">
            RT
          </span>
          <div>
            <strong>RTRugs OS</strong>
            <small>{surface === "desktop" ? "Windows 桌面入口" : "浏览器备用入口"}</small>
          </div>
        </div>
        <div className="environment-strip" aria-label="当前运行环境">
          <span className="environment-badge">本地环境</span>
          <span className="simulation-badge">模拟运行</span>
          <span className="connection-state">
            <i aria-hidden="true" /> 等待接口
          </span>
        </div>
        <div className="top-actions">
          <button className="pause-button" type="button">
            全局暂停
          </button>
          <button className="icon-button" type="button" aria-label="打开全局搜索">
            搜索
          </button>
          <button className="avatar-button" type="button" aria-label="打开账号菜单">
            负责人
          </button>
        </div>
      </header>

      <div className="application-grid">
        <nav className="center-navigation" aria-label="五个业务中心">
          {centers.map((center, index) => (
            <button
              className={center === activeCenter ? "active" : undefined}
              key={center}
              onClick={() => setActiveCenter(center)}
              type="button"
            >
              <span className="nav-index">0{index + 1}</span>
              <span>{center}</span>
            </button>
          ))}
          <div className="navigation-footer">
            <span>第一阶段</span>
            <strong>0.1.0</strong>
          </div>
        </nav>

        <main className="workspace">
          <section className="workspace-heading">
            <div>
              <p className="eyebrow">当前业务工作区</p>
              <h1>{activeCenter}</h1>
              <p>{centerDescriptions[activeCenter]}</p>
            </div>
            <time dateTime="2026-07-19">批次 1 · 工程底座</time>
          </section>

          <section className="status-grid" aria-label="今日态势">
            <article>
              <span>待审批</span>
              <strong>0</strong>
              <small>尚未建立正式事项</small>
            </article>
            <article>
              <span>运行任务</span>
              <strong>0</strong>
              <small>真实流程未启用</small>
            </article>
            <article>
              <span>异常等待</span>
              <strong>0</strong>
              <small>当前无异常</small>
            </article>
            <article>
              <span>模型成本</span>
              <strong>$0.00</strong>
              <small>付费调用未授权</small>
            </article>
          </section>

          <section className="conversation-panel">
            <div className="system-message">
              <span className="message-label">总指挥系统</span>
              <p>
                工程底座正在建设中。你可以在这里提出运营目标；在后续批次中，系统会先展示对象、版本、费用、权限、审批和风险，再建立正式任务。
              </p>
            </div>
            <div className="empty-timeline">
              <div aria-hidden="true">◇</div>
              <p>当前没有正式对话或任务记录</p>
              <small>本页只显示合成数据，不读取真实客户、订单或文件。</small>
            </div>
            <form
              className="composer"
              onSubmit={(event) => {
                event.preventDefault();
              }}
            >
              <label htmlFor="command-draft">向总指挥系统说明你的目标</label>
              <textarea
                id="command-draft"
                onChange={(event) => setDraft(event.target.value)}
                placeholder="例如：分析本周地毯市场机会，并先生成候选清单……"
                rows={3}
                value={draft}
              />
              <div>
                <span>Enter 发送 · Shift＋Enter 换行</span>
                <button disabled={draft.trim().length === 0} type="submit">
                  形成操作草案
                </button>
              </div>
            </form>
          </section>
        </main>

        <aside className="context-panel" aria-label="当前操作草案和风险提示">
          <section>
            <p className="eyebrow">当前操作草案</p>
            <h2>尚未形成草案</h2>
            <p>对话内容不会直接成为正式任务。确认前，系统会在这里展示结构化影响。</p>
            <dl>
              <div>
                <dt>外部动作</dt>
                <dd>关闭</dd>
              </div>
              <div>
                <dt>付费调用</dt>
                <dd>未授权</dd>
              </div>
              <div>
                <dt>人工审批</dt>
                <dd>按事项判断</dd>
              </div>
            </dl>
          </section>
          <section className="foundation-card">
            <p className="eyebrow">工程连接</p>
            <h2>本地接口</h2>
            <code>{apiBaseUrl}</code>
            <p>完成本地依赖启动后，将由健康检查更新连接状态。</p>
          </section>
          <section className="risk-card">
            <span>边界提示</span>
            <p>批次 1 不允许真实连接器、真实文件、真实订单或自动审批。</p>
          </section>
        </aside>
      </div>

      <footer className="statusbar">
        <span>同步状态：尚未连接</span>
        <span>应用版本：0.1.0</span>
        <span>最后更新：未同步</span>
        <strong>模拟运行</strong>
      </footer>
    </div>
  );
}
